import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Business,
  Security,
  Speed,
  SupportAgent,
  CheckCircle,
  ArrowForward,
  Cloud,
  Devices,
  Public,
  Analytics,
  Router,
  CellTower,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CTASection from '../components/common/CTASection';

const EnterpriseSolutions = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const benefits = [
    'Dedicated account team with 24/7 enterprise support',
    'Custom SLAs tailored to your business needs',
    'Priority network routing for mission-critical applications',
    'Advanced DDoS protection and threat monitoring',
    'Compliance-ready solutions (GDPR, HIPAA, PCI-DSS)',
    'Flexible contract terms and pricing models',
  ];

  return (
    <Box>
      <PageHeader 
        title="Enterprise Solutions" 
        subtitle="Custom communication infrastructure for global enterprises"
      />

      {/* Benefits */}
      <Box sx={{ py: 8, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Why Choose Us" 
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Why Choose Our Enterprise Solutions?
                </Typography>
                <List>
                  {benefits.map((benefit, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#e60108' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={benefit} 
                        primaryTypographyProps={{ sx: { color: '#191919' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card sx={{ p: 4, bgcolor: '#e60108', color: '#fefefe', borderRadius: 4 }}>
                  <Typography variant="h4" gutterBottom fontWeight="800">
                    Ready to Transform Your Enterprise?
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                    Schedule a consultation with our enterprise team to discuss your specific requirements.
                  </Typography>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={() => navigate('/contact')}
                      sx={{
                        bgcolor: '#fefefe',
                        color: '#e60108',
                        fontWeight: 700,
                        '&:hover': { 
                          bgcolor: '#fefefe', 
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                        },
                      }}
                    >
                      Contact Enterprise Team
                    </Button>
                  </motion.div>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Transform Your Enterprise" 
        subtitle="Get a custom solution designed for your global operations"
        buttonText="Request Consultation"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default EnterpriseSolutions;